import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PATHWAY } from "@/constants/testIds/clinic";

export default function DualPathwayCards() {
    return (
        <section
            data-testid={PATHWAY.section}
            className="relative"
            style={{ backgroundColor: "hsl(var(--bg-chalk))" }}
        >
            <div className="ihx-container py-20 lg:py-28">
                <div className="max-w-2xl">
                    <span className="ihx-eyebrow">Two clinical pathways · One practice</span>
                    <h2 className="ihx-h-section mt-5">
                        Whether you’re reversing a diagnosis or engineering longevity, the protocol is precise — and <span className="ihx-italic">personal.</span>
                    </h2>
                    <p className="ihx-lead mt-6 max-w-xl">
                        Every patient enters through one of two pathways. Each begins with a
                        comprehensive baseline and converges on a single principle: care that
                        is measured, monitored, and recalibrated.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-7">
                    <PathwayCard
                        order="order-1"
                        testid={PATHWAY.reversalCard}
                        ctaTestId={PATHWAY.reversalCta}
                        emphasis
                        eyebrow="Pathway A · Clinical"
                        title={<>Disease <span className="ihx-italic">reversal.</span></>}
                        subtitle="For patients living with a diagnosis they want to reverse — not merely manage."
                        pillars={[
                            { t: "PCOS & Hormonal Health", d: "Cycle restoration, insulin sensitisation, androgen recalibration." },
                            { t: "Metabolic Health", d: "Type 2 diabetes, prediabetes, fatty liver — biomarker-guided reversal." },
                            { t: "Cardiac Rehabilitation", d: "Lipoprotein optimisation, blood pressure, post-event recovery." },
                        ]}
                        cta="Begin a reversal protocol"
                    />
                    <PathwayCard
                        order="order-2"
                        testid={PATHWAY.longevityCard}
                        ctaTestId={PATHWAY.longevityCta}
                        eyebrow="Pathway B · Performance"
                        title={<>Longevity & <span className="ihx-italic">optimisation.</span></>}
                        subtitle="For high-performers engineering the next forty years of biology."
                        pillars={[
                            { t: "Biomarker Panels", d: "ApoB, hsCRP, HbA1c, hormones, micronutrients." },
                            { t: "Cellular Energy", d: "Mitochondrial conditioning, sleep, circadian alignment." },
                            { t: "Cardiorespiratory Fitness", d: "VO₂max, zone-2 protocols, strength as a vital sign." },
                        ]}
                        cta="Design a longevity protocol"
                    />
                </div>
            </div>
        </section>
    );
}

function PathwayCard({ order, testid, ctaTestId, emphasis, eyebrow, title, subtitle, pillars, cta }) {
    return (
        <article
            data-testid={testid}
            className={`ihx-card relative ${order} ${
                emphasis ? "lg:col-span-7" : "lg:col-span-5"
            } p-7 md:p-9 lg:p-11 flex flex-col`}
            style={{
                background: emphasis
                    ? "radial-gradient(80% 100% at 0% 0%, hsl(var(--brand-gold-soft) / 0.4) 0%, transparent 60%), hsl(var(--bg-white-warm))"
                    : "hsl(var(--bg-white-warm))",
            }}
        >
            <span className="ihx-eyebrow">{eyebrow}</span>
            <h3
                className={`font-serif mt-4 leading-[1.02] tracking-[-0.02em] ${
                    emphasis ? "text-[36px] lg:text-[44px]" : "text-[30px] lg:text-[36px]"
                }`}
            >
                {title}
            </h3>
            <p className="text-[15.5px] leading-[1.65] text-[hsl(var(--ink-soft))] mt-4 max-w-md">
                {subtitle}
            </p>

            <div className="mt-9 flex-1 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-7 lg:gap-x-8">
                {pillars.map((p) => (
                    <div key={p.t} className="border-t border-[hsl(var(--border-stone))] pt-4">
                        <div className="font-serif text-[17px] text-[hsl(var(--brand-ink-deep))] leading-tight">
                            {p.t}
                        </div>
                        <p className="text-[13.5px] leading-[1.6] text-[hsl(var(--ink-muted))] mt-2">
                            {p.d}
                        </p>
                    </div>
                ))}
            </div>

            <div className="mt-10 pt-6 border-t border-[hsl(var(--border-stone))] flex items-center justify-between gap-4">
                <Link
                    to="/apply"
                    data-testid={ctaTestId}
                    className="ihx-btn-link"
                >
                    {cta} <ArrowRight size={15} strokeWidth={1.7} />
                </Link>
                <span className="hidden md:inline text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                    Hybrid · In-clinic · Virtual
                </span>
            </div>
        </article>
    );
}
